// HeadlineTiles.js — Three summary stat cards for base scenario (hedged first/largest)
import React from 'react';
import { TrendingUp, Target, BarChart3 } from 'lucide-react';

/**
 * Format a number as compact currency.
 * @param {number} value
 * @param {string} ccy
 * @returns {string}
 */
function fmtCurrency(value, ccy = 'USD') {
  if (value === null || value === undefined) return '--';
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  const sym = ccy === 'USD' ? '$' : ccy === 'GBP' ? '\u00a3' : ccy === 'EUR' ? '\u20ac' : ccy + ' ';
  if (abs >= 1e6) return `${sign}${sym}${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}${sym}${(abs / 1e3).toFixed(0)}K`;
  return `${sign}${sym}${abs.toFixed(0)}`;
}

/**
 * Format as percentage with sign.
 * @param {number} value - decimal (e.g. 0.08 = 8%)
 * @returns {string}
 */
function fmtPct(value) {
  if (value === null || value === undefined) return '--';
  const pct = (value * 100).toFixed(2);
  return `${value >= 0 ? '+' : ''}${pct}%`;
}

/**
 * Headline tiles showing base-scenario hedged projections.
 * Guardrail: hedged is always first/largest; only base scenario shown.
 * @param {{ baseResult: Object, benchmark: Object, baseCurrency: string }} props
 */
const HeadlineTiles = ({ baseResult, benchmark, baseCurrency = 'USD' }) => {
  if (!baseResult) return null;

  const endValue = baseResult.endValueHedged;
  const annReturn = baseResult.annualizedReturnHedged;
  const benchmarkEnd = benchmark ? benchmark.endValue : 0;
  const alpha = endValue - benchmarkEnd;

  const tiles = [
    {
      label: 'End Value (Hedged)',
      value: fmtCurrency(endValue, baseCurrency),
      sublabel: 'Base scenario, 10-year',
      icon: TrendingUp,
      large: true,
    },
    {
      label: 'Annualised Return (Hedged)',
      value: fmtPct(annReturn),
      sublabel: 'Compound annual',
      icon: Target,
      large: false,
    },
    {
      label: `vs ${benchmark ? benchmark.label : 'Benchmark'} (Hedged)`,
      value: fmtCurrency(alpha, baseCurrency),
      sublabel: fmtPct(benchmarkEnd > 0 ? alpha / benchmarkEnd : 0) + ' cumulative alpha',
      icon: BarChart3,
      large: false,
      positive: alpha >= 0,
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
      {tiles.map((tile, i) => (
        <div
          key={i}
          className={`bg-white border border-zinc-200 rounded-none p-5 ${tile.large ? 'sm:col-span-1 ring-1 ring-indigo-100' : ''}`}
          role="figure"
          aria-label={tile.label}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
              {tile.label}
            </span>
            <tile.icon className="w-4 h-4 text-zinc-400" />
          </div>
          <p className={`font-mono text-xl font-semibold tabular-nums ${
            tile.positive === false ? 'text-red-600' : tile.positive === true ? 'text-emerald-600' : 'text-zinc-900'
          }`}>
            {tile.value}
          </p>
          <p className="font-mono text-[10px] text-zinc-400 mt-1">{tile.sublabel}</p>
        </div>
      ))}
    </div>
  );
};

export default HeadlineTiles;
